import { get, isEmpty, has } from 'lodash';
import { SECTION_ITEM_TYPES, LEARNING_ASSET_TYPES } from '../Constants';
import { getUserLearningAssetReviewAnswers } from '../actions';

/**
 * Slides
 */
import ShortEssaySlide from '../containers/workspaceSlides/ShortEssaySlide';
import ChooseOneSlide from '../containers/workspaceSlides/ChooseOneSlide';
import ChooseOnePathSlide from '../containers/workspaceSlides/ChooseOnePathSlide';
import LikertSlide from '../containers/workspaceSlides/LikertSlide';
import ChooseManySlide from '../containers/workspaceSlides/ChooseManySlide';
import ThisOrThatPermutationSlide from '../containers/workspaceSlides/ThisOrThatPermutationSlide';
import ThisOrThatResultSlide from '../containers/workspaceSlides/ThisOrThatResultSlide';
import ThisOrThatResultChart from '../containers/workspaceSlides/ThisOrThatResultChart';
import UserInviteSlide from '../containers/workspaceSlides/UserInviteSlide/UserInviteSlide';
import ChooseOneResultsSlide from '../containers/workspaceSlides/ChooseOneResultsSlide';
import ShortEssayResultSlide from '../containers/workspaceSlides/ShortEssayResultSlide';
import FileUploadSlide from '../containers/workspaceSlides/FileUploadSlide';
import DynamicRichTextSlide from '../containers/workspaceSlides/DynamicRichTextSlide';
import ChooseOneQuizSlide from '../containers/workspaceSlides/ChooseOneQuizSlide';
import QuizResultSlide from '../containers/workspaceSlides/QuizResultSlide';
import ShowPreviousAnswersSlide from '../containers/workspaceSlides/ShowPreviousAnswersSlide';
import ShowPreviousAndChooseOneSlide from '../containers/workspaceSlides/ShowPreviousAndChooseOneSlide';
import ReviewPreviousActivitiesSlide from '../containers/workspaceSlides/ReviewPreviousActivitiesSlide';
import EndSessionSlide from '../containers/workspaceSlides/EndSessionSlide';
import EndWorkflowSlide from '../containers/workspaceSlides/EndWorkflowSlide';

/**
 * Returns true if sectionItem should be visible
 * NOTE: treats sectionItems with no isActive property as "Active"
 * @param {Object} sectionItem
 * @return {boolean}
 */
export const isSectionItemActive = (sectionItem) =>
    sectionItem !== undefined &&
    sectionItem !== null &&
    sectionItem.isUnlocked === true &&
    (!('isActive' in sectionItem) || sectionItem.isActive === true);

/**
 *
 * @param {Object} learningAsset
 * @param {Object} learningAsset.sectionItemById
 * @param {Array} learningAsset.sections
 * @param {Array} learningAsset.sections
 * @param {String} sectionItemId
 * @return {Object}
 * @private
 */
export const getSectionItemById = (learningAsset, sectionItemId) => {
    let sectionItemToReturn = {};
    let itemFound = false;

    if (has(learningAsset, 'sectionItemById') && sectionItemId in learningAsset.sectionItemById) {
        return learningAsset.sectionItemById[sectionItemId];
    }

    let sectionItemLength = 0;
    const sectionLength = get(learningAsset, 'sections', []).length;
    for (let sectionIndex = 0; sectionIndex < sectionLength; sectionIndex += 1) {
        const section = learningAsset.sections[sectionIndex];
        sectionItemLength = section.sectionItems.length;

        for (let sectionItemIndex = 0; sectionItemIndex < sectionItemLength; sectionItemIndex += 1) {
            const sectionItem = section.sectionItems[sectionItemIndex];
            const currentSectionItemId = sectionItem && sectionItem.id;
            if (currentSectionItemId === sectionItemId) {
                sectionItemToReturn = sectionItem;
                itemFound = true;
                break;
            }
        }

        if (itemFound) {
            break;
        }
    }

    return sectionItemToReturn;
};

/**
 *
 * @param {Object} section
 * @param {Array} section.sectionItems
 * @return {String}
 */
export const getFirstAvailableSectionItemId = (section) => {
    let firstAvailableSectionItemId = '';
    for (let i = 0, length = get(section, 'sectionItems.length', 0); i < length; i += 1) {
        const sectionItem = section.sectionItems[i];
        if (isSectionItemActive(sectionItem)) {
            firstAvailableSectionItemId = sectionItem.id;
            break;
        }
    }

    return firstAvailableSectionItemId;
};

/**
 *
 * @param {Object} section
 * @param {Array} section.sectionItems
 * @return {String}
 */
export const getLastAvailableSectionItemId = (section) => {
    let lastAvailableSectionItemId = '';

    for (let i = get(section, 'sectionItems.length', 0) - 1; i >= 0; i -= 1) {
        const sectionItem = section.sectionItems[i];
        if (isSectionItemActive(sectionItem)) {
            lastAvailableSectionItemId = sectionItem.id;
            break;
        }
    }

    return lastAvailableSectionItemId;
};

/**
 *
 * @param {Array} sectionItems
 * @param {number} indexToStartAfter
 * @return {boolean}
 */
export const remainingSectionItemsAreVisible = (sectionItems, indexToStartAfter) => {
    const begin = indexToStartAfter + 1;
    const end = get(sectionItems, 'length', 0);

    let currentSectionItem;
    for (let i = begin; i < end; i += 1) {
        currentSectionItem = sectionItems[i];
        if (isSectionItemActive(currentSectionItem)) {
            return true;
        }
    }

    return false;
};

/**
 *
 * @param {Object} userLearningAsset
 * @param {Object} [userLearningAsset.track]
 * @param {string|Array} sectionItemId
 * @returns {Object}
 * @private
 */
export const getSectionItemContentById = (userLearningAsset, sectionItemId) => {
    let sectionItem;
    let content = {};

    const learningAsset = get(userLearningAsset, 'track', {});
    if (Array.isArray(sectionItemId)) {
        content = [];
        sectionItemId.forEach((sectionItem) => {
            let item;
            let id;
            if (typeof sectionItem === 'object') {
                id = sectionItem.itemId;
                item = getSectionItemById(learningAsset, sectionItem.itemId);
            } else {
                id = sectionItem;
                item = getSectionItemById(learningAsset, sectionItem);
            }

            if (!isEmpty(item)) {
                content.push({ ...item.content, id, name: item.name });
            }
        });
    } else {
        sectionItem = getSectionItemById(learningAsset, sectionItemId);
        if (!isEmpty(sectionItem)) {
            ({ content } = sectionItem);
            content.name = sectionItem.name;
        }
    }
    return content;
};

/**
 * Returns the completedSection by sectionItemId
 * @param {Object} completedSections
 * @param {string|Array} sectionItemId
 * @return {Object}
 * @private
 */
export const getSectionItemCompleteDataById = (completedSections, sectionItemId) => {
    if (Array.isArray(sectionItemId)) {
        const data = {};
        sectionItemId.forEach((item) => {
            if (typeof item === 'object') {
                data[item.itemId] = get(completedSections, `${item.itemId}`, {});
            } else {
                data[item] = get(completedSections, `${item}`, {});
            }
        });
        return data;
    }
    return get(completedSections, `${sectionItemId}`, {});
};

export const addDefaultVerbResource = (content) => {
    if (!content) {
        return content;
    }
    const { blocks, resources } = content;
    const newContent = { ...content };

    if (blocks && blocks.length) {
        const contentBlocks = (blocks || []).map((block) => {
            let resourceTag = '';
            if (block.description && block.description.includes('<strong>Resource</strong>')) {
                resourceTag = '<strong>Resource</strong>';
            }

            if (block.description && block.description.includes('<strong>Resources</strong>')) {
                resourceTag = '<strong>Resources</strong>';
            }

            if (resourceTag) {
                if (block.description && block.description.split(resourceTag)[1]) {
                    let blockDescription = block.description;

                    // look for Paths-crafted micro-learning experience in resources
                    const hasMiroLearningText = block.description
                        .split(resourceTag)[1]
                        .includes('Paths-crafted micro-learning experience');
                    if (!hasMiroLearningText) {
                        blockDescription = `${
                            block.description.split(resourceTag)[0]
                        } ${resourceTag} <p>Paths-crafted micro-learning experience.</p> ${
                            block.description.split(resourceTag)[1]
                        }`;
                    }
                    // eslint-disable-next-line prefer-destructuring
                    block.description = blockDescription;
                }
            }
            return block;
        });
        newContent.blocks = contentBlocks;
    }

    if (resources) {
        const hasMicroLearningText = resources.includes('Paths-crafted micro-learning experience');
        if (!hasMicroLearningText) {
            const contentResources = `<p>Paths-crafted micro-learning experience.</p> ${resources}`;
            newContent.resources = contentResources;
        }
    }

    return newContent;
};

export const getSlideObject = (params) => {
    const { trackData, workspaceProps, eventHandlers, isPreview, isRefSheet = false, pathsContext = {} } = params;
    const { formattedSectionItemType, sectionItem, nextSectionName, completedData, sectionItems, sectionItemName } =
        trackData;
    const {
        currentUserTrack,
        currentUser,
        files,
        completeSections,
        isReviewWorkspace,
        isWelcomeWorkspace,
        isLearningSampler = false,
        learningSamplerAnswers = {},
    } = workspaceProps;
    const { preText, postText, responseVariableName } = sectionItem;
    const { handleFileChange, onChangeHandler, onSubmit, setResponseVariables, setRoles } = eventHandlers;
    let newSlideObject;

    if (formattedSectionItemType === SECTION_ITEM_TYPES.CHOOSE_ONE_CONTENT) {
        const chooseContent = get(sectionItem, 'content');
        newSlideObject = {};
        newSlideObject.component = ChooseOneSlide;

        newSlideObject.componentProps = {
            content: addDefaultVerbResource(chooseContent),
            onChange: onChangeHandler,
            completedData,
            nextSectionName,
            currentUser,
            isWelcomeWorkspace
        };
        if (isPreview) {
            newSlideObject.componentProps = {
                content: addDefaultVerbResource(chooseContent)
            };
        }
    } else if (formattedSectionItemType === SECTION_ITEM_TYPES.CHOOSE_ONE_PATH_CONTENT) {
        const chooseContent = get(sectionItem, 'content');
        newSlideObject = {};
        newSlideObject.component = ChooseOnePathSlide;
        newSlideObject.componentProps = {
            content: addDefaultVerbResource(chooseContent),
            onChange: onChangeHandler,
            completedData,
            nextSectionName,
            currentUser,
            isWelcomeWorkspace,
            setRoles,
            type: SECTION_ITEM_TYPES.CHOOSE_ONE_PATH_CONTENT
        };
        if (isPreview) {
            newSlideObject.componentProps = {
                content: addDefaultVerbResource(chooseContent)
            };
        }
    } else if (formattedSectionItemType === SECTION_ITEM_TYPES.CHOOSE_MANY_CONTENT) {
        const chooseContent = get(sectionItem, 'content');
        newSlideObject = {};
        newSlideObject.component = ChooseManySlide;
        newSlideObject.componentProps = {
            onChange: onChangeHandler,
            content: addDefaultVerbResource(chooseContent),
            completedData,
            nextSectionName,
            currentUser,
            isWelcomeWorkspace,
            preText,
            postText
        };
        if (isPreview) {
            newSlideObject.componentProps = {
                content: addDefaultVerbResource(chooseContent),
                completedData: {}
            };
        }
    } else if (formattedSectionItemType === SECTION_ITEM_TYPES.SHORT_ESSAY_CONTENT) {
        const essayInfo = get(sectionItem, 'content');
        if (essayInfo) {
            newSlideObject = {};
            newSlideObject.component = ShortEssaySlide;
            newSlideObject.componentProps = {
                onChange: onChangeHandler,
                essay: essayInfo,
                completedData,
                nextSectionName,
                currentUser
            };
        }
        if (isPreview) {
            newSlideObject.componentProps = {
                essay: essayInfo
            };
        }
    } else if (formattedSectionItemType === SECTION_ITEM_TYPES.SHORT_ESSAY_RESULT) {
        newSlideObject = {};
        newSlideObject.component = ShortEssayResultSlide;
        newSlideObject.componentProps = {
            onChange: onChangeHandler,
            content: sectionItem.content,
            completedData,
            nextSectionName,
            currentUser,
            shortEssayContent: getSectionItemContentById(currentUserTrack, sectionItem.content.items),
            shortEssayQuestionData: getSectionItemCompleteDataById(completeSections, sectionItem.content.items),
            currentUserTrack
        };
        if (isPreview) {
            const shortEssayContent = getSectionItemContentById(currentUserTrack, sectionItem.content.items);
            newSlideObject.componentProps = {
                content: sectionItem.content,
                shortEssayContent: isEmpty(shortEssayContent) ? [] : shortEssayContent
            };
        }
    } else if (formattedSectionItemType === SECTION_ITEM_TYPES.LIKERT_CONTENT) {
        newSlideObject = {};
        newSlideObject.component = LikertSlide;
        newSlideObject.componentProps = {
            onChange: onChangeHandler,
            content: addDefaultVerbResource(sectionItem.content),
            completedData,
            nextSectionName,
            currentUser
        };
        if (isPreview) {
            newSlideObject.componentProps = {
                content: addDefaultVerbResource(sectionItem.content)
            };
        }
    } else if (formattedSectionItemType === SECTION_ITEM_TYPES.THIS_OR_THAT_PERMUTATION_CONTENT) {
        newSlideObject = {};
        newSlideObject.component = ThisOrThatPermutationSlide;
        newSlideObject.componentProps = {
            onChange: onChangeHandler,
            content: addDefaultVerbResource(sectionItem.content),
            completedData,
            nextSectionName,
            currentUser
        };
        if (isPreview) {
            newSlideObject.componentProps = {
                content: addDefaultVerbResource(sectionItem.content)
            };
        }
    } else if (formattedSectionItemType === SECTION_ITEM_TYPES.THIS_OR_THAT_RESULTS_CONTENT) {
        newSlideObject = {};
        newSlideObject.component = ThisOrThatResultSlide;
        newSlideObject.componentProps = {
            onChange: onChangeHandler,
            content: addDefaultVerbResource(sectionItem.content),
            completedData,
            nextSectionName,
            currentUser,
            abQuestionContent: getSectionItemContentById(currentUserTrack, sectionItem.content.questionSectionItemId),
            abQuestionData: getSectionItemCompleteDataById(completeSections, sectionItem.content.questionSectionItemId)
        };
        if (isPreview) {
            newSlideObject.componentProps = {
                content: addDefaultVerbResource(sectionItem.content),
                abQuestionContent: getSectionItemContentById(
                    currentUserTrack,
                    sectionItem.content.questionSectionItemId
                ),
                abQuestionData: getSectionItemCompleteDataById({}, sectionItem.content.questionSectionItemId)
            };
        }
    } else if (formattedSectionItemType === SECTION_ITEM_TYPES.THIS_OR_THAT_RESULTS_CHART) {
        newSlideObject = {};
        newSlideObject.component = ThisOrThatResultChart;
        newSlideObject.componentProps = {
            onChange: onChangeHandler,
            content: addDefaultVerbResource(sectionItem.content),
            completedData,
            nextSectionName,
            currentUser,
            abQuestionContent: getSectionItemContentById(currentUserTrack, sectionItem.content.questionSectionItemId),
            abQuestionData: getSectionItemCompleteDataById(completeSections, sectionItem.content.questionSectionItemId)
        };
        if (isPreview) {
            newSlideObject.componentProps = {
                content: addDefaultVerbResource(sectionItem.content),
                abQuestionContent: getSectionItemContentById(
                    currentUserTrack,
                    sectionItem.content.questionSectionItemId
                ),
                abQuestionData: getSectionItemCompleteDataById({}, sectionItem.content.questionSectionItemId)
            };
        }
    } else if (formattedSectionItemType === SECTION_ITEM_TYPES.RICH_TEXT_CONTENT) {
        const slideComponentContent = addDefaultVerbResource({ blocks: [sectionItem.content] });
        newSlideObject = {};
        newSlideObject.component = DynamicRichTextSlide;
        newSlideObject.componentProps = {
            onChange: onChangeHandler,
            content: slideComponentContent,
            completedData,
            nextSectionName,
            currentUser
        };
        if (isPreview) {
            newSlideObject.componentProps = {
                content: slideComponentContent
            };
        }
    } else if (formattedSectionItemType === SECTION_ITEM_TYPES.USER_INVITE_CONTENT) {
        newSlideObject = {};
        newSlideObject.component = UserInviteSlide;
        newSlideObject.componentProps = {
            onChange: onChangeHandler,
            content: addDefaultVerbResource(sectionItem.content),
            completedData,
            nextSectionName,
            currentUser,
            totalParticipants: get(currentUserTrack, 'track.participants', []).length,
            currentSectionItemId: sectionItem.id
        };
        if (isPreview) {
            newSlideObject.componentProps = {
                content: addDefaultVerbResource(sectionItem.content),
                totalParrticipants: [],
                currentSectionItemId: sectionItem.id
            };
        }
    } else if (formattedSectionItemType === SECTION_ITEM_TYPES.CHOOSE_ONE_RESULTS_CONTENT) {
        newSlideObject = {};
        newSlideObject.component = ChooseOneResultsSlide;
        const referencedSectionItem = getSectionItemById(
            currentUserTrack.track,
            sectionItem.content.chooseOneSectionItemId
        );
        newSlideObject.componentProps = {
            onChange: onChangeHandler,
            content: addDefaultVerbResource(sectionItem.content),
            completedData,
            nextSectionName,
            currentUser,
            globalResults: referencedSectionItem.globalResults,
            chooseOneContent: referencedSectionItem.content,
            chooseOneData: getSectionItemCompleteDataById(completeSections, sectionItem.content.chooseOneSectionItemId)
        };
        if (isPreview) {
            newSlideObject.componentProps = {
                content: addDefaultVerbResource(sectionItem.content),
                globalResults: referencedSectionItem.globalResults,
                chooseOneContent: referencedSectionItem.content,
                chooseOneData: getSectionItemCompleteDataById({}, sectionItem.content.chooseOneSectionItemId)
            };
        }
    } else if (formattedSectionItemType === SECTION_ITEM_TYPES.FILE_UPLOAD_CONTENT) {
        newSlideObject = {};
        newSlideObject.component = FileUploadSlide;
        newSlideObject.componentProps = {
            onChange: onChangeHandler,
            content: addDefaultVerbResource(sectionItem.content),
            completedData,
            nextSectionName,
            currentUser,
            parentId: get(currentUserTrack, '_id', ''),
            parentType: 'UserTrack',
            handleFileChange,
            filesById: files
        };
        if (isPreview) {
            newSlideObject.componentProps = {
                content: addDefaultVerbResource(sectionItem.content)
            };
        }
    } else if (
        [SECTION_ITEM_TYPES.DYNAMIC_RICH_TEXT_CONTENT, SECTION_ITEM_TYPES.WRAP_UP].includes(formattedSectionItemType)
    ) {
        newSlideObject = {};
        newSlideObject.component = DynamicRichTextSlide;
        newSlideObject.componentProps = {
            onChange: onChangeHandler,
            content: addDefaultVerbResource(sectionItem.content),
            completedData,
            nextSectionName,
            currentUser,
            isWrapUp: formattedSectionItemType === SECTION_ITEM_TYPES.WRAP_UP
        };
        if (isPreview) {
            newSlideObject.componentProps = {
                content: addDefaultVerbResource(sectionItem.content),
                isWrapUp: formattedSectionItemType === SECTION_ITEM_TYPES.WRAP_UP
            };
        }
    } else if (formattedSectionItemType === SECTION_ITEM_TYPES.CHOOSE_ONE_QUIZ) {
        newSlideObject = {};
        newSlideObject.component = ChooseOneQuizSlide;
        newSlideObject.componentProps = {
            onChange: onChangeHandler,
            onAnswerSubmit: onSubmit,
            content: addDefaultVerbResource(sectionItem.content),
            completedData,
            nextSectionName,
            currentUser,
            sectionItems,
            currentSectionItem: sectionItem,
            requireSubmit: true
        };
        if (isPreview) {
            newSlideObject.componentProps = {
                content: addDefaultVerbResource(sectionItem.content),
                currentSectionItem: sectionItem,
                sectionItems
            };
        }
    } else if (formattedSectionItemType === SECTION_ITEM_TYPES.QUIZ_RESULTS) {
        newSlideObject = {};
        newSlideObject.component = QuizResultSlide;
        newSlideObject.componentProps = {
            onChange: onChangeHandler,
            content: addDefaultVerbResource(sectionItem.content),
            completedData: completeSections,
            nextSectionName,
            currentUser,
            sectionItems
        };
        if (isPreview) {
            newSlideObject.componentProps = {
                content: addDefaultVerbResource(sectionItem.content),
                sectionItems
            };
        }
    } else if (formattedSectionItemType === SECTION_ITEM_TYPES.SHOW_PREVIOUS_ANSWERS) {
        const associatedQuestions = {};
        const questionIds = [];
        get(sectionItem.content, 'questionsToDisplay', []).forEach((item) => {
            if (get(item, 'questionId', '')) {
                associatedQuestions[item.questionId] = getSectionItemById(currentUserTrack.track, item.questionId);
                questionIds.push(item.questionId);
            }
        });

        newSlideObject = {};
        newSlideObject.component = ShowPreviousAnswersSlide;
        newSlideObject.componentProps = {
            onChange: onChangeHandler,
            content: addDefaultVerbResource(sectionItem.content),
            nextSectionName,
            currentUser,
            completedData,
            associatedQuestions,
            currentUserAnswers: getSectionItemCompleteDataById(
                isLearningSampler ? learningSamplerAnswers : completeSections,
                questionIds
            ),
            currentUserTrack,
            isRefSheet
        };

        if (isPreview && !isRefSheet) {
            newSlideObject.componentProps = {
                content: addDefaultVerbResource(sectionItem.content),
                associatedQuestions
            };
        }
    } else if (formattedSectionItemType === SECTION_ITEM_TYPES.SHOW_PREVIOUS_ANSWERS_CHOOSE_ONE) {
        const associatedQuestions = {};
        const questionIds = [];
        get(sectionItem.content, 'questionsToDisplay', []).forEach((item) => {
            if (get(item, 'questionId', '')) {
                associatedQuestions[item.questionId] = getSectionItemById(currentUserTrack.track, item.questionId);
                questionIds.push(item.questionId);
            }
        });

        const chooseContent = get(sectionItem, 'choiceContent');

        newSlideObject = {};
        newSlideObject.component = ShowPreviousAndChooseOneSlide;
        newSlideObject.componentProps = {
            onChange: onChangeHandler,
            content: addDefaultVerbResource(sectionItem.content),
            chooseContent,
            nextSectionName,
            currentUser,
            completedData,
            associatedQuestions,
            currentUserAnswers: getSectionItemCompleteDataById(
                isLearningSampler ? learningSamplerAnswers : completeSections,
                questionIds
            ),
            currentUserTrack
        };

        if (isPreview && !isRefSheet) {
            newSlideObject.componentProps = {
                content: addDefaultVerbResource(sectionItem.content),
                chooseContent,
                associatedQuestions
            };
        }
    } else if (formattedSectionItemType === SECTION_ITEM_TYPES.REVIEW_PREV_ACTIVITIES) {
        newSlideObject = {};
        newSlideObject.component = ReviewPreviousActivitiesSlide;
        newSlideObject.componentProps = {
            fetchReviewData: () => getUserLearningAssetReviewAnswers( pathsContext,{ userLearningAssetId : currentUserTrack._id }),
            onChange: onChangeHandler,
            content: addDefaultVerbResource(sectionItem.content),
            completedData,
            nextSectionName,
            currentUser,
            currentUserTrack
        };
    } else if (formattedSectionItemType === SECTION_ITEM_TYPES.END_SESSION_CONTENT) {
        newSlideObject = {};
        newSlideObject.component = EndSessionSlide;
        newSlideObject.componentProps = {
            onChange: onChangeHandler,
            completedData,
            currentUser
        };
    } else if (formattedSectionItemType === SECTION_ITEM_TYPES.END_WORKFLOW) {
        newSlideObject = {};
        newSlideObject.component = EndWorkflowSlide;
        newSlideObject.componentProps = {
            onChange: () => {},
            content: addDefaultVerbResource(sectionItem.content),
            currentUser
        };
    } else {
        // if the sectionItem.type does not match any of the items above, we just return, because it should not
        // be included in the slide
        console.log(`Excluding SectionItem from render. [SectionItem.Type: ${sectionItem.type}]`);
        return null;
    }
    if (isPreview) {
        newSlideObject.componentProps.handleAwaitingSubmitState = () => null;
        newSlideObject.componentProps.handleOptionSelect = () => null;
        newSlideObject.componentProps.canMoveToNext = () => null;
        newSlideObject.componentProps.onChange = () => null;
        newSlideObject.componentProps.currentUser = currentUser;
        newSlideObject.componentProps.name = sectionItemName;
        newSlideObject.id = sectionItem.id;
    }
    if (isReviewWorkspace || isPreview) {
        newSlideObject.componentProps.hideBookmark = true;
    }
    newSlideObject.componentProps.sectionId = sectionItem.sectionId;
    newSlideObject.componentProps.sectionItemId = sectionItem.id;
    newSlideObject.componentProps.learningAssetType = currentUserTrack.type;
    newSlideObject.componentProps.setResponseVariables = setResponseVariables;
    newSlideObject.componentProps.responseVariableName = responseVariableName;

    if (newSlideObject?.componentProps?.learningAssetType === LEARNING_ASSET_TYPES.REFLECTION) {
        newSlideObject.targetParticipantRole = sectionItem.targetParticipantRole || '';
    }
    return newSlideObject;
};

/**
 *
 * @param currentUserLearningAsset
 * @param workspaceMountDateTime
 * @returns {{userLearningAssetId: *, body: {updatedOn: Date, status: (*|null)}}}
 */

export const buildUserLearningAssetMetadataPayload = (currentUserLearningAsset, workspaceMountDateTime) => {
    let updateTimeToPass = new Date(currentUserLearningAsset.updatedOn);
    if (workspaceMountDateTime > updateTimeToPass) {
        updateTimeToPass = workspaceMountDateTime;
    }
    return {
        updatedOn: updateTimeToPass,
        status: currentUserLearningAsset.status || null
    };
};
